import { Vector3, Matrix4, BufferGeometry, BufferAttribute } from 'three';

/**
 * Berechnet die sichtbaren Vertices eines 3D-Objekts und projiziert sie auf den Bildschirm
 * @param {Object} object - Three.js Object3D
 * @param {Object} camera - Three.js Camera
 * @param {number} imageWidth - Bildbreite
 * @param {number} imageHeight - Bildhöhe
 * @returns {Array<{x: number, y: number}>} Array von 2D-Punkten
 */
const projectObjectVertices = (object, camera, imageWidth, imageHeight, vertexSampleRate = 2) => {
    if (!object || !camera) return [];

    object.updateMatrixWorld(true);
    camera.updateMatrixWorld(true);
    
    const correctAspect = imageWidth / imageHeight;
    const originalAspect = camera.aspect;
    const needsAspectUpdate = Math.abs(camera.aspect - correctAspect) > 0.001;
    
    if (camera.isPerspectiveCamera && needsAspectUpdate) {
        camera.aspect = correctAspect;
        camera.updateProjectionMatrix();
    }

    const screenPoints = [];
    const viewMatrix = camera.matrixWorldInverse;
    const projectionMatrix = camera.projectionMatrix;

    // Verwende Vertex-Sampling für Performance
    // Standard: vertexSampleRate = 2 (jeder 2. Vertex) für bessere Genauigkeit
    const tempWorldPoint = new Vector3();
    const tempViewPoint = new Vector3();
    const tempNDCPoint = new Vector3();
    
    object.traverse((child) => {
        if (child.isMesh && child.geometry) {
            const geometry = child.geometry;
            const positionAttribute = geometry.attributes.position;
            
            if (positionAttribute) {
                const worldMatrix = child.matrixWorld;
                const vertexCount = positionAttribute.count;
                
                // Projiziere Vertices mit Sampling
                // Kleinere vertexSampleRate = mehr Punkte = genauere Kontur
                for (let i = 0; i < vertexCount; i += vertexSampleRate) {
                    tempWorldPoint.fromBufferAttribute(positionAttribute, i);
                    tempWorldPoint.applyMatrix4(worldMatrix);

                    // Transformiere zu Kamera-Koordinaten
                    tempViewPoint.copy(tempWorldPoint);
                    tempViewPoint.applyMatrix4(viewMatrix);
                    
                    // Prüfe ob Punkt vor der Kamera liegt
                    if (camera.isPerspectiveCamera) {
                        if (tempViewPoint.z >= -camera.near) {
                            continue;
                        }
                    }
                    
                    // Projiziere mit der Projektionsmatrix
                    tempNDCPoint.copy(tempViewPoint);
                    tempNDCPoint.applyMatrix4(projectionMatrix);
                    
                    // Perspektivische Division
                    if (camera.isPerspectiveCamera && Math.abs(tempNDCPoint.w) > 0.0001) {
                        tempNDCPoint.x /= tempNDCPoint.w;
                        tempNDCPoint.y /= tempNDCPoint.w;
                    }
                    
                    // Transformiere NDC zu Pixel-Koordinaten
                    const pixelX = ((tempNDCPoint.x + 1) / 2) * imageWidth;
                    const pixelY = ((1 - tempNDCPoint.y) / 2) * imageHeight;
                    
                    // Nur gültige Punkte innerhalb des Bildes (mit Margin für bessere Kontur)
                    if (isFinite(pixelX) && isFinite(pixelY)) {
                        const margin = Math.max(imageWidth, imageHeight) * 0.1; // Erhöhte Margin für bessere Erfassung
                        if (pixelX >= -margin && pixelX <= imageWidth + margin &&
                            pixelY >= -margin && pixelY <= imageHeight + margin) {
                            screenPoints.push({ x: pixelX, y: pixelY });
                        }
                    }
                }
            }
        }
    });

    // Stelle Aspect-Ratio wieder her
    if (camera.isPerspectiveCamera && needsAspectUpdate) {
        camera.aspect = originalAspect;
        camera.updateProjectionMatrix();
    }

    return screenPoints;
};

/**
 * Berechnet den Convex Hull einer Punktmenge (Graham Scan Algorithmus)
 * @param {Array<{x: number, y: number}>} points - Array von 2D-Punkten
 * @returns {Array<{x: number, y: number}>} Punkte des Convex Hulls im Uhrzeigersinn
 */
const calculateConvexHull = (points, maxPoints = 50) => {
    if (points.length < 3) return points;
    
    // PERFORMANCE: Reduziere Punktanzahl für Echtzeit-Performance
    let workingPoints = points;
    if (points.length > maxPoints) {
        // Grid-basiertes Sampling für bessere Verteilung
        const gridSize = Math.ceil(Math.sqrt(points.length / maxPoints));
        const grid = new Map();
        
        for (const point of points) {
            const gridX = Math.floor(point.x / gridSize);
            const gridY = Math.floor(point.y / gridSize);
            const key = `${gridX},${gridY}`;
            
            if (!grid.has(key)) {
                grid.set(key, point);
            }
        }
        
        workingPoints = Array.from(grid.values());
        
        // Falls immer noch zu viele, nehme jeden N-ten Punkt
        if (workingPoints.length > maxPoints) {
            const step = Math.ceil(workingPoints.length / maxPoints);
            workingPoints = workingPoints.filter((_, i) => i % step === 0);
        }
    }

    // Finde den untersten Punkt (und bei Gleichstand den linkesten)
    let bottomPoint = workingPoints[0];
    let bottomIndex = 0;
    for (let i = 1; i < workingPoints.length; i++) {
        if (workingPoints[i].y < bottomPoint.y || 
            (workingPoints[i].y === bottomPoint.y && workingPoints[i].x < bottomPoint.x)) {
            bottomPoint = workingPoints[i];
            bottomIndex = i;
        }
    }

    // Sortiere Punkte nach Polarwinkel relativ zum bottomPoint
    const sortedPoints = workingPoints.map((p, i) => ({
        ...p,
        index: i
    })).filter((_, i) => i !== bottomIndex);

    sortedPoints.sort((a, b) => {
        const angleA = Math.atan2(a.y - bottomPoint.y, a.x - bottomPoint.x);
        const angleB = Math.atan2(b.y - bottomPoint.y, b.x - bottomPoint.x);
        return angleA - angleB;
    });

    // Graham Scan
    const hull = [bottomPoint];
    
    for (const point of sortedPoints) {
        while (hull.length > 1) {
            const p1 = hull[hull.length - 2];
            const p2 = hull[hull.length - 1];
            
            // Prüfe ob p2 eine Rechtskurve bildet (cross product)
            const cross = (p2.x - p1.x) * (point.y - p1.y) - (p2.y - p1.y) * (point.x - p1.x);
            if (cross <= 0) {
                hull.pop();
            } else {
                break;
            }
        }
        hull.push(point);
    }

    return hull;
};

/**
 * Vereinfacht ein Polygon durch Entfernen von Punkten, die zu nahe beieinander liegen
 * @param {Array<{x: number, y: number}>} polygon - Polygon-Punkte
 * @param {number} minDistance - Minimale Distanz zwischen Punkten
 * @returns {Array<{x: number, y: number}>} Vereinfachtes Polygon
 */
const simplifyPolygon = (polygon, minDistance = 2) => {
    if (polygon.length <= 3) return polygon;

    // PERFORMANCE: Verwende quadratische Distanz statt sqrt für bessere Performance
    const minDistanceSq = minDistance * minDistance;
    const simplified = [polygon[0]];
    
    for (let i = 1; i < polygon.length; i++) {
        const lastPoint = simplified[simplified.length - 1];
        const currentPoint = polygon[i];
        
        const dx = currentPoint.x - lastPoint.x;
        const dy = currentPoint.y - lastPoint.y;
        const distanceSq = dx * dx + dy * dy; // Quadratische Distanz
        
        if (distanceSq >= minDistanceSq) {
            simplified.push(currentPoint);
        }
    }
    
    // Prüfe auch den letzten Punkt zum ersten
    const firstPoint = polygon[0];
    const lastPoint = simplified[simplified.length - 1];
    const dx = firstPoint.x - lastPoint.x;
    const dy = firstPoint.y - lastPoint.y;
    const distanceSq = dx * dx + dy * dy;
    
    if (distanceSq < minDistanceSq && simplified.length > 2) {
        simplified.pop();
    }
    
    return simplified.length >= 3 ? simplified : polygon;
};

/**
 * Berechnet ein Polygon für ein 3D-Objekt (Kontur)
 * @param {Object} object - Three.js Object3D
 * @param {Object} camera - Three.js Camera
 * @param {number} imageWidth - Bildbreite
 * @param {number} imageHeight - Bildhöhe
 * @returns {Array<{x: number, y: number}>} Polygon-Punkte im Uhrzeigersinn
 */
export const calculateObjectPolygon = (object, camera, imageWidth, imageHeight, vertexSampleRate = 2, maxHullPoints = 500, minDistanceFactor = 200) => {
    if (!object || !camera || !imageWidth || !imageHeight) {
        return null;
    }

    // Stelle sicher, dass die Kamera-Matrix aktualisiert ist
    camera.updateMatrixWorld(true);
    camera.updateProjectionMatrix();

    // Projiziere Vertices mit Sampling
    // vertexSampleRate kann angepasst werden: kleiner = genauer aber langsamer
    // Standard: 2 (jeder 2. Vertex) für bessere Genauigkeit
    const screenPoints = projectObjectVertices(object, camera, imageWidth, imageHeight, vertexSampleRate);
    
    if (screenPoints.length < 3) {
        return null;
    }

    // Berechne Convex Hull mit begrenzter Punktanzahl
    // maxHullPoints kann angepasst werden: größer = genauer aber langsamer
    // Standard: 500 für detailliertere Konturen
    const hull = calculateConvexHull(screenPoints, maxHullPoints);
    
    if (hull.length < 3) {
        return null;
    }

    // Vereinfache das Polygon
    // minDistanceFactor kann angepasst werden: kleiner = genauer aber langsamer
    // Standard: 200 für weniger aggressive Vereinfachung
    const minDistance = Math.max(1, Math.min(imageWidth, imageHeight) / minDistanceFactor);
    const simplified = simplifyPolygon(hull, minDistance);
    
    // Stelle sicher, dass alle Punkte innerhalb des Bildes liegen
    const clamped = simplified.map(p => ({
        x: Math.max(0, Math.min(imageWidth, p.x)),
        y: Math.max(0, Math.min(imageHeight, p.y))
    }));

    return clamped.length >= 3 ? clamped : null;
};

/**
 * Berechnet Polygone für mehrere Objekte
 * @param {Array<Object>} objects - Array von Three.js Object3D
 * @param {Object} camera - Three.js Camera
 * @param {number} imageWidth - Bildbreite
 * @param {number} imageHeight - Bildhöhe
 * @returns {Array<Object>} Array von Polygonen mit objectIndex
 */
export const calculateMultiplePolygons = (objects, camera, imageWidth, imageHeight, vertexSampleRate = 2, maxHullPoints = 500, minDistanceFactor = 200) => {
    if (!objects || !Array.isArray(objects) || objects.length === 0) {
        return [];
    }

    if (!camera || !imageWidth || !imageHeight) {
        return [];
    }

    // Stelle sicher, dass die Kamera-Matrix aktualisiert ist
    camera.updateMatrixWorld(true);
    camera.updateProjectionMatrix();

    const polygons = [];
    
    objects.forEach((object, index) => {
        if (!object) return;
        
        // Stelle sicher, dass das Objekt-Matrix aktualisiert ist
        object.updateMatrixWorld(true);
        
        const polygon = calculateObjectPolygon(object, camera, imageWidth, imageHeight, vertexSampleRate, maxHullPoints, minDistanceFactor);
        if (polygon && polygon.length >= 3) {
            polygons.push({
                objectIndex: index,
                points: polygon
            });
        }
    });
    
    return polygons;
};

